import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Copy, 
  Search, 
  Settings, 
  Download, 
  ChevronRight,
  Layers,
  Zap,
  Shield,
  Target,
  ArrowRight,
  Keyboard,
  Globe
} from "lucide-react";

const CHROME_WEB_STORE_URL =
  "https://chromewebstore.google.com/detail/tab-finder/ojjcagfaflhiboegdneahjkddcmmpafp?hl=en-US&utm_source=ext_sidebar";

const RadarLogo = ({ className = "h-10 w-auto" }) => (
  <img
    src="/tabfinder-logo.png"
    alt="Tab Finder"
    className={className}
  />
);

// Navigation Header
const Header = () => (
  <header className="sticky top-0 z-50 w-full border-b border-border/50 bg-background/80 backdrop-blur-md">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex h-16 items-center">
        <RadarLogo className="h-10 w-auto" />
        <nav className="hidden md:flex items-center gap-8 ml-auto mr-4">
          <a href="#features" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Features</a>
          <a href="#how-it-works" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">How It Works</a>
          <a href="#installation" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Install</a>
        </nav>
        <div className="flex items-center gap-3">
          <Button size="sm" asChild className="bg-accent hover:bg-accent-dark text-accent-foreground">
            <a href={CHROME_WEB_STORE_URL} target="_blank" rel="noopener noreferrer">
              <Download className="w-4 h-4 mr-2" />
              Start Free Trial
            </a>
          </Button>
        </div>
      </div>
    </div>
  </header>
);

// Hero Section
const HeroSection = () => (
  <section className="relative overflow-hidden bg-gradient-to-b from-secondary/50 to-background py-20 lg:py-28">
    {/* Background pattern */}
    <div className="absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
      backgroundSize: '32px 32px'
    }} />
    
    {/* Floating radar circles */}
    <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse" />
    <div className="absolute bottom-10 right-10 w-96 h-96 bg-accent/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
    
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <div className="space-y-8 animate-fade-up lg:mt-[20px]">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full border border-primary/20">
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Chrome Extension</span>
          </div>
          
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight tracking-tight">
            Kill Duplicate Tabs &amp; Find Any Page In Under 1 Second
          </h1>
          
          <p className="text-lg text-muted-foreground max-w-lg leading-relaxed">
            Tab Finder Pro scans every Chrome window, finds true duplicates with smart URL matching, and lets you jump to any tab instantly - even with 1000+ tabs open.
          </p>
          
          <div className="flex flex-wrap gap-4">
            <Button size="lg" asChild className="bg-accent hover:bg-accent-dark text-accent-foreground shadow-accent-glow">
              <a href={CHROME_WEB_STORE_URL} target="_blank" rel="noopener noreferrer">
                <Download className="w-5 h-5 mr-2" />
                Start 7-Day Free Trial
              </a>
            </Button>
            <a href="#live-demo">
              <Button size="lg" variant="outline" className="group">
                Watch Live Demo
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </div>
          
          <p className="pt-4 text-sm text-muted-foreground">
            $7/month or $59/year · 100% local (no tracking) · Manifest V3 ready
          </p>
        </div>
        
        {/* Live demo embed */}
        <div id="live-demo" className="relative animate-fade-up lg:-mt-[70px]" style={{ animationDelay: '0.2s' }}>
          <iframe
            src="/demo"
            title="Tab Finder live demo"
            className="w-full h-[640px] border-0 bg-transparent"
            loading="lazy"
            scrolling="no"
          />
          <p className="mt-3 text-center text-sm text-muted-foreground">
            Interactive demo with mock data. Install the extension for real functionality.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Features Section
const FeaturesSection = () => {
  const features = [
    {
      icon: <Copy className="w-6 h-6" />,
      title: "Smart Duplicate Detection",
      description: "Finds real duplicates across all windows using intelligent URL canonicalization. Ignores tracking junk and normalizes URLs automatically.",
      color: "primary"
    },
    {
      icon: <Search className="w-6 h-6" />,
      title: "Instant Global Tab Search",
      description: "Search open tabs by title, URL, or hostname with relevance-based results and quick switching to the tab you want.",
      color: "accent"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "One-Click Cleanup",
      description: "Close duplicate groups in one action (Merge All Duplicates) or clean up individual groups from the duplicates view.",
      color: "primary"
    },
    {
      icon: <Keyboard className="w-6 h-6" />,
      title: "Keyboard-Powered Navigation",
      description: "Press / to jump into search, use arrow keys to navigate results, and press Enter to focus a tab fast.",
      color: "accent"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Privacy-First",
      description: "Minimal permissions. 100% local processing. No data leaves your browser, no tracking scripts.",
      color: "primary"
    },
    {
      icon: <Download className="w-6 h-6" />,
      title: "Export & Backup",
      description: "Export your current tab list to JSON or CSV for backup, handoff, or analysis.",
      color: "accent"
    }
  ];

  return (
    <section id="features" className="py-20 lg:py-28 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Badge variant="outline" className="mb-4 px-4 py-1.5">
            <Target className="w-3 h-3 mr-2" />
            Features
          </Badge>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4 lg:whitespace-nowrap">
            Everything You Need to Tame Your Tabs
          </h2>
          <p className="text-lg text-muted-foreground">
            Built for people who live in the browser and can&apos;t afford to lose context.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <Card 
              key={index} 
              className="group hover:shadow-lg hover:border-primary/20 transition-all duration-300"
            >
              <CardHeader>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  feature.color === 'primary' 
                    ? 'bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground' 
                    : 'bg-accent/10 text-accent group-hover:bg-accent group-hover:text-accent-foreground'
                } transition-colors`}>
                  {feature.icon}
                </div>
                <CardTitle className="text-lg">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "Install Extension",
      description: "Install Tab Finder Pro from the Chrome Web Store. No complex setup required."
    },
    {
      number: "02",
      title: "Scan Your Tabs",
      description: "Scan all your tabs across every window in one click."
    },
    {
      number: "03",
      title: "Clean Up Duplicates",
      description: "Review duplicate groups and clean them up with a single click or handle them individually."
    },
    {
      number: "04",
      title: "Search Anytime",
      description: "Use the toolbar icon or keyboard shortcut to search all your tabs instantly whenever you need it."
    }
  ];

  return (
    <section id="how-it-works" className="py-20 lg:py-28 bg-secondary/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Badge variant="outline" className="mb-4 px-4 py-1.5">
            <Globe className="w-3 h-3 mr-2" />
            How It Works
          </Badge>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Get Started in Seconds
          </h2>
          <p className="text-lg text-muted-foreground">
            Built for people who live in the browser and can&apos;t afford to lose context.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-[60%] w-full h-0.5 bg-gradient-to-r from-primary/30 to-transparent" />
              )}
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold text-xl mb-6 shadow-lg">
                  {step.number}
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-3">
                  {step.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Installation Section
const InstallationSection = () => (
  <section id="installation" className="py-20 lg:py-28 bg-background">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-10">
        <Badge variant="outline" className="px-4 py-1.5 mb-4">
          <Settings className="w-3 h-3 mr-2" />
          Installation
        </Badge>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-3">
          Installation Walkthrough
        </h2>
        <p className="text-lg text-muted-foreground">
          Watch the full setup walkthrough for Tab Finder.
        </p>
      </div>

      <div className="max-w-5xl mx-auto">
        <div className="relative w-full overflow-hidden rounded-2xl border border-border shadow-lg bg-black">
          <div className="relative pb-[56.25%] h-0">
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src="https://www.youtube.com/embed/rN2_dpdttyA"
              title="Tab Finder installation walkthrough"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);


// CTA Section
const CTASection = () => (
  <section className="py-20 lg:py-28 bg-gradient-to-br from-primary to-primary-dark text-primary-foreground">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-2xl mx-auto space-y-8">
        <RadarLogo className="h-20 w-auto mx-auto animate-pulse-glow" />
        <h2 className="font-heading text-3xl sm:text-4xl font-bold">
          Ready To Stop Drowning In Tabs?
        </h2>
        <p className="text-lg text-primary-foreground/80">
          Start your 7-day free trial of Tab Finder Pro. If it doesn&apos;t make your browser feel lighter within a week, just uninstall - no hard feelings.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Button size="lg" asChild className="bg-accent hover:bg-accent-dark text-accent-foreground">
            <a href={CHROME_WEB_STORE_URL} target="_blank" rel="noopener noreferrer">
              <Download className="w-5 h-5 mr-2" />
              Start 7-Day Free Trial
            </a>
          </Button>
        </div>
        <p className="text-sm text-primary-foreground/80">
          $7/month or $59/year after trial · Cancel anytime
        </p>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="py-12 bg-foreground text-background">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <RadarLogo className="h-10 w-auto" />
        <p className="text-sm text-muted-foreground">
          Built for tab hoarders everywhere. Manifest V3 compliant.
        </p>
        <div className="flex items-center gap-6">
          <Link to="/contact" className="text-sm text-muted-foreground hover:text-background transition-colors">
            Contact
          </Link>
          <Link to="/privacy" className="text-sm text-muted-foreground hover:text-background transition-colors">
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  </footer>
);

// Main Landing Page Component
export default function LandingPage() {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <FeaturesSection />
        <HowItWorksSection />
        <InstallationSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
}
